import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { IconButton } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import UiTypography from "components/base/UiTypography";
import type { AssignmentSource } from "modules/aiMapping/aiAssignmentSources";
import type { ProductOption } from "../measurementTypes";
import type { AssignedSource, CostOption, ProductDraft } from "./aiAssignmentTypes";
import { selectSx } from "./aiAssignmentTypes";
import ProductPicker from "./ProductPicker";
import SourceChip from "./SourceChip";
import SourcePicker from "./SourcePicker";

export default function ProductDraftCard({
  draft,
  products,
  sources,
  additionalCosts,
  sourceById,
  isLinearFeetAllowed,
  onToggleOpen,
  onChangeProduct,
  onAddSource,
  onRemoveJobSource,
  onRemoveCostSource,
  onChangeAdditionalCost,
  onRemoveAdditionalCost,
  onRemoveProduct,
  onChangeStallMode,
  onAddCostRow,
}: {
  draft: ProductDraft;
  products: ProductOption[];
  sources: AssignmentSource[];
  additionalCosts: CostOption[];
  sourceById: Map<string, AssignmentSource>;
  isLinearFeetAllowed: (productId: string | null) => boolean;
  onToggleOpen: () => void;
  onChangeProduct: (productId: string | null) => void;
  onAddSource: (costId: string | undefined, sourceId: string) => void;
  onRemoveJobSource: (assignment: AssignedSource) => void;
  onRemoveCostSource: (costId: string, assignment: AssignedSource) => void;
  onChangeAdditionalCost: (costRowId: string, additionalCostId: string | null) => void;
  onRemoveAdditionalCost: (costRowId: string) => void;
  onRemoveProduct: () => void;
  onChangeStallMode: (assignmentId: string, mode: "count" | "linear_ft", costId?: string) => void;
  onAddCostRow: () => void;
}) {
  const product = products.find((option) => option.id === draft.productId);
  const linearFeetOk = isLinearFeetAllowed(draft.productId);
  const sourceCount =
    draft.sources.length + draft.costs.reduce((total, cost) => total + cost.sources.length, 0);

  const renderChip = (assignment: AssignedSource, costId?: string) => {
    const source = sourceById.get(assignment.sourceId);
    if (!source) return null;
    return (
      <SourceChip
        key={assignment.id}
        source={source}
        assignment={assignment}
        linearFeetAllowed={linearFeetOk}
        onRemove={() =>
          costId ? onRemoveCostSource(costId, assignment) : onRemoveJobSource(assignment)
        }
        onStallModeChange={(mode) => onChangeStallMode(assignment.id, mode, costId)}
      />
    );
  };

  return (
    <MDBox sx={{ border: "1px solid #dce3ea", borderRadius: "10px", overflow: "hidden" }}>
      <MDBox p="10px 12px" display="flex" alignItems="center" gap="8px" bgcolor="#fafbfc">
        <MDBox sx={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={onToggleOpen}>
          <UiTypography uiVariant="body-sm" sx={{ fontWeight: 700 }}>
            {product?.name ?? "Select product"}
          </UiTypography>
          <UiTypography uiVariant="caption" sx={{ color: "#667085" }}>
            {sourceCount} {sourceCount === 1 ? "assignment" : "assignments"} · {draft.costs.length}{" "}
            additional {draft.costs.length === 1 ? "cost" : "costs"}
          </UiTypography>
        </MDBox>
        <IconButton size="small" onClick={onRemoveProduct} title="Remove product assignment">
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={onToggleOpen}>
          <ExpandMoreIcon
            fontSize="small"
            sx={{
              transform: draft.open ? "rotate(180deg)" : "none",
              transition: "transform 150ms ease",
            }}
          />
        </IconButton>
      </MDBox>

      {draft.open && (
        <MDBox p="12px" display="flex" flexDirection="column" gap="10px">
          <ProductPicker value={draft.productId} products={products} onChange={onChangeProduct} />

          <UiTypography uiVariant="caption" sx={{ color: "#667085", fontWeight: 700 }}>
            Job size sources
          </UiTypography>
          {draft.sources.map((assignment) => renderChip(assignment))}
          <SourcePicker
            value=""
            sources={sources}
            disabled={!draft.productId}
            onChange={(sourceId) => onAddSource(undefined, sourceId)}
          />

          {draft.costs.map((cost) => (
            <MDBox
              key={cost.id}
              p="10px"
              sx={{ border: "1px solid #e5e7eb", borderRadius: "9px", bgcolor: "#fafbfc" }}
            >
              <MDBox display="flex" gap="8px" alignItems="center" mb="8px">
                <MDBox
                  component="select"
                  value={cost.costId ?? ""}
                  onChange={(event: any) =>
                    onChangeAdditionalCost(cost.id, event.target.value || null)
                  }
                  sx={selectSx}
                  aria-label="Additional cost"
                >
                  <option value="">Select additional cost…</option>
                  {additionalCosts.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </MDBox>
                <IconButton
                  size="small"
                  onClick={() => onRemoveAdditionalCost(cost.id)}
                  title="Remove additional cost"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </MDBox>
              <MDBox display="flex" flexDirection="column" gap="6px">
                {cost.sources.map((assignment) => renderChip(assignment, cost.id))}
              </MDBox>
              <MDBox mt="8px">
                <SourcePicker
                  value=""
                  sources={sources}
                  disabled={!draft.productId}
                  onChange={(sourceId) => onAddSource(cost.id, sourceId)}
                />
              </MDBox>
            </MDBox>
          ))}

          <MDButton
            size="small"
            variant="text"
            color="info"
            startIcon={<AddIcon />}
            onClick={onAddCostRow}
            sx={{ alignSelf: "flex-start", px: 0.5 }}
          >
            Add additional cost
          </MDButton>
        </MDBox>
      )}
    </MDBox>
  );
}
